// Store (record) reads for lists, the map and the detail page. Ports of Record.swift (displayName, subtitle,
// searchText, daysSinceLastVisit), AppStore.pinColorHex (AppStore.swift:204), MapTab+Store.swift (filteredRecords,
// sorted, legend), MapSession.swift (MapFilter, RecordListSort), MapFilterSheet.swift (quick presets),
// Records+Store.swift (historyEntries, upcomingActivities), Components+Store.swift (displayTitle, cardFieldsLine) and
// RecordDetailTabs.HistoryFilter.
//
// Values iOS keeps local-only (visit count, last visit, last activity, next scheduled) come from `DerivedStats`
// (derived.ts); pass the map `recomputeDerivedStats` / `useDerivedStats()` returns.

import {
  ACTIVITY_TYPE_TITLES as ACTIVITY_TITLE,
  PRIORITIES,
  PRIORITY_COLORS,
  PRIORITY_SCORES,
  PRIORITY_TITLES,
  RECORD_TYPES,
  RECORD_TYPE_TITLES,
  type MapColorize,
  type Priority,
  type RecordType,
} from '@/contracts/enums';
import { customValuesToMap, fieldValueDisplay } from '@/contracts/fieldValue';
import {
  addressOneLine,
  recordDisplayName,
  type ActivityPayload,
  type CommPayload,
  type CustomFieldPayload,
  type PipelinePayload,
  type RecordPayload,
  type UserPayload,
  type VisitResultPayload,
} from '@/contracts/models';
import { isUuid } from '@/contracts/uuid';
import { addDays, calendarDaysBetween, startOfDayOffset, wholeDaysBetween } from './calendar';
import { effectiveMs, statsOf, type DerivedStats, type RecordStats } from './derived';
import { formatMoney } from './format';
import { distanceMeters, type Coordinate } from './geo';
import { pipelineFor, stageFor } from './pipelines';
import { byId, compareLocalized, compareScalars, fullName, hasId, idKey, idSet, lookup, ms, sameId, trimSpaces } from './util';

export { addressOneLine, recordDisplayName };

const MAX_ORDER = Number.MAX_SAFE_INTEGER;

/** `Record.subtitle`: "Job title · Company" for contacts, the one-line address otherwise. */
export function recordSubtitle(r: Pick<RecordPayload, 'type' | 'jobTitle' | 'company' | 'address'>): string {
  if (r.type === 'contact') return [r.jobTitle, r.company].filter((s) => s !== '').join(' · ');
  return addressOneLine(r.address);
}

/** `Record.searchText`: name, company, job title, address, phone, email, tags and notes, lower-cased. */
export function searchText(r: RecordPayload): string {
  return [r.name, r.company, r.jobTitle, addressOneLine(r.address), r.phone, r.email, r.tags.join(' '), r.notes]
    .join(' ')
    .toLowerCase();
}

/** `AppStore.search(_:in:)`: substring match of the trimmed, lower-cased query; an empty query keeps everything. */
export function searchRecords<R extends RecordPayload>(records: readonly R[], text: string): R[] {
  const q = trimSpaces(text).toLowerCase();
  if (q === '') return [...records];
  return records.filter((r) => searchText(r).includes(q));
}

/** `Record.daysSinceLastVisit`: calendar days from the last visit's day to today (undefined = never visited). */
export function daysSinceVisit(stats: Pick<RecordStats, 'lastVisitAt'>, now: Date = new Date()): number | undefined {
  return stats.lastVisitAt ? calendarDaysBetween(stats.lastVisitAt, now) : undefined;
}

/** Whole days since the last activity of any type (`dateComponents([.day], from:to:)`); undefined = none ever. */
export function daysSinceActivity(stats: Pick<RecordStats, 'lastActivityAt'>, now: Date = new Date()): number | undefined {
  return stats.lastActivityAt ? wholeDaysBetween(stats.lastActivityAt, now) : undefined;
}

// MARK: - Pins and legend

/** The gray used for "no value" pins and chips. */
export const PIN_GRAY = '#8C919E';

/** `pinColorHex` in record-type mode (and `RecordType.tint`). */
export const RECORD_TYPE_PIN_COLORS: Readonly<Record<RecordType, string>> = {
  lead: '#F96A3C',
  account: '#102A5E',
  contact: '#7D4DBF',
};

/** Days-since-visit pin colors: ≤ 7 days green, ≤ 30 amber, older red, never gray. */
export const DAYS_SINCE_VISIT_COLORS = { recent: '#29A85C', month: '#F5B400', old: '#DB4038', never: PIN_GRAY } as const;

/** Lookups the pin, legend, filter and sort functions share (build once per data change). */
export interface RecordLookups {
  stats?: DerivedStats;
  visitResults: ReadonlyMap<string, VisitResultPayload>;
  users: ReadonlyMap<string, UserPayload>;
  pipelines: readonly PipelinePayload[];
  now: Date;
}

export function recordLookups(input: {
  stats?: DerivedStats;
  visitResults: readonly VisitResultPayload[];
  users: readonly UserPayload[];
  pipelines: readonly PipelinePayload[];
  now?: Date;
}): RecordLookups {
  const lk: RecordLookups = {
    visitResults: byId(input.visitResults),
    users: byId(input.users),
    pipelines: input.pipelines,
    now: input.now ?? new Date(),
  };
  if (input.stats) lk.stats = input.stats;
  return lk;
}

/** `AppStore.pinColorHex(for:mode:)`. */
export function pinColorHex(record: RecordPayload, mode: MapColorize, lk: RecordLookups): string {
  switch (mode) {
    case 'visitResult':
      return lookup(lk.visitResults, statsOf(lk.stats, record.id).lastVisitResultId)?.colorHex ?? PIN_GRAY;
    case 'stage':
      return stageFor(record, lk.pipelines)?.colorHex ?? PIN_GRAY;
    case 'priority':
      return PRIORITY_COLORS[record.priority] ?? PIN_GRAY;
    case 'owner':
      return lookup(lk.users, record.ownerId)?.colorHex ?? PIN_GRAY;
    case 'recordType':
      return RECORD_TYPE_PIN_COLORS[record.type] ?? PIN_GRAY;
    case 'daysSinceVisit': {
      const d = daysSinceVisit(statsOf(lk.stats, record.id), lk.now);
      if (d === undefined) return DAYS_SINCE_VISIT_COLORS.never;
      return d <= 7 ? DAYS_SINCE_VISIT_COLORS.recent : d <= 30 ? DAYS_SINCE_VISIT_COLORS.month : DAYS_SINCE_VISIT_COLORS.old;
    }
  }
}

/** The text on a pin (MapCanvasView): the priority letter in priority mode, else the visit count when above 0. */
export function pinLabel(record: RecordPayload, mode: MapColorize, lk: Pick<RecordLookups, 'stats'>): string | undefined {
  if (mode === 'priority') return record.priority;
  const n = statsOf(lk.stats, record.id).visitCount;
  return n > 0 ? String(n) : undefined;
}

/** `legendLabel(for:mode:)`: the legend row a record falls in and that row's sort order. */
export function legendLabel(record: RecordPayload, mode: MapColorize, lk: RecordLookups): { label: string; order: number } {
  switch (mode) {
    case 'visitResult': {
      const r = lookup(lk.visitResults, statsOf(lk.stats, record.id).lastVisitResultId);
      return r ? { label: r.name, order: r.order } : { label: 'No visit yet', order: MAX_ORDER };
    }
    case 'stage': {
      const s = stageFor(record, lk.pipelines);
      return s ? { label: s.name, order: s.order } : { label: 'No stage', order: MAX_ORDER };
    }
    case 'priority':
      return { label: PRIORITY_TITLES[record.priority], order: 10 - (PRIORITY_SCORES[record.priority] ?? 0) };
    case 'owner': {
      const u = lookup(lk.users, record.ownerId);
      return u ? { label: fullName(u), order: 0 } : { label: 'Unassigned', order: MAX_ORDER };
    }
    case 'recordType':
      return { label: RECORD_TYPE_TITLES[record.type], order: Math.max(0, RECORD_TYPES.indexOf(record.type)) };
    case 'daysSinceVisit': {
      const d = daysSinceVisit(statsOf(lk.stats, record.id), lk.now);
      if (d === undefined) return { label: 'Never visited', order: 3 };
      if (d <= 7) return { label: '0–7 days', order: 0 };
      if (d <= 30) return { label: '8–30 days', order: 1 };
      return { label: '31+ days', order: 2 };
    }
  }
}

export interface LegendEntry {
  label: string;
  colorHex: string;
  count: number;
  order: number;
}

/** `legend(for:records:)`: one row per label (color of the first record in it), sorted by order, then label. */
export function legend(mode: MapColorize, records: readonly RecordPayload[], lk: RecordLookups): LegendEntry[] {
  const rows = new Map<string, LegendEntry>();
  for (const r of records) {
    const { label, order } = legendLabel(r, mode, lk);
    const e = rows.get(label);
    if (e) e.count += 1;
    else rows.set(label, { label, colorHex: pinColorHex(r, mode, lk), count: 1, order });
  }
  return [...rows.values()].sort((a, b) => a.order - b.order || compareScalars(a.label, b.label));
}

// MARK: - MapFilter

/**
 * `MapFilter` (MapSession.swift), shared by the Stores list and the map. Ids may be any case. Two web additions
 * from WEB_PLAN.md §5.5: `pipelineIds` (stores on those boards) and `includeUnassigned` (the "Unassigned" owner).
 */
export interface MapFilter {
  types: readonly RecordType[];
  pipelineIds: readonly string[];
  stageIds: readonly string[];
  visitResultIds: readonly string[];
  priorities: readonly Priority[];
  ownerIds: readonly string[];
  includeUnassigned: boolean;
  territoryIds: readonly string[];
  /** Inclusive bounds on calendar days since the last visit (never-visited stores fail any bound). */
  daysSinceVisitMin?: number;
  daysSinceVisitMax?: number;
  /** Inclusive bounds on whole days since the last activity; no activity ever counts as infinitely old. */
  daysSinceActivityMin?: number;
  daysSinceActivityMax?: number;
  /** Any of these tags (exact match). */
  tags: readonly string[];
  onlyMine: boolean;
  neverVisited: boolean;
  hasScheduledActivity: boolean;
  /** A scheduled activity due by the end of today, overdue included ("Follow-up Needed"). */
  followUpDue: boolean;
}

export const EMPTY_FILTER: Readonly<MapFilter> = Object.freeze({
  types: [],
  pipelineIds: [],
  stageIds: [],
  visitResultIds: [],
  priorities: [],
  ownerIds: [],
  includeUnassigned: false,
  territoryIds: [],
  tags: [],
  onlyMine: false,
  neverVisited: false,
  hasScheduledActivity: false,
  followUpDue: false,
});

/** `MapFilter.activeCount`: criteria in use (the badge on the filter button). */
export function filterActiveCount(f: MapFilter): number {
  let n = 0;
  if (f.types.length > 0) n += 1;
  if (f.pipelineIds.length > 0) n += 1;
  if (f.stageIds.length > 0) n += 1;
  if (f.visitResultIds.length > 0) n += 1;
  if (f.priorities.length > 0) n += 1;
  if (f.ownerIds.length > 0 || f.includeUnassigned) n += 1;
  if (f.territoryIds.length > 0) n += 1;
  if (f.daysSinceVisitMin !== undefined || f.daysSinceVisitMax !== undefined) n += 1;
  if (f.daysSinceActivityMin !== undefined || f.daysSinceActivityMax !== undefined) n += 1;
  if (f.tags.length > 0) n += 1;
  if (f.onlyMine) n += 1;
  if (f.neverVisited) n += 1;
  if (f.hasScheduledActivity) n += 1;
  if (f.followUpDue) n += 1;
  return n;
}

export function isFilterActive(f: MapFilter): boolean {
  return filterActiveCount(f) > 0;
}

export interface FilterContext {
  stats?: DerivedStats;
  pipelines: readonly PipelinePayload[];
  /** The viewer's profile id ("Only mine"). */
  viewerId: string | null | undefined;
  now?: Date;
}

/** `AppStore.filteredRecords(_:in:)`: pass the visible stores; every criterion in use must match. */
export function applyFilter<R extends RecordPayload>(records: readonly R[], filter: MapFilter, ctx: FilterContext): R[] {
  if (!isFilterActive(filter)) return [...records];
  const now = ctx.now ?? new Date();
  const endOfToday = startOfDayOffset(now, 1).getTime();
  const types = new Set(filter.types);
  const priorities = new Set(filter.priorities);
  const pipelineIds = idSet(filter.pipelineIds);
  const stageIds = idSet(filter.stageIds);
  const resultIds = idSet(filter.visitResultIds);
  const ownerIds = idSet(filter.ownerIds);
  const territoryIds = idSet(filter.territoryIds);
  const tags = new Set(filter.tags);
  const ownerBounded = filter.ownerIds.length > 0 || filter.includeUnassigned;
  const activityBounded = filter.daysSinceActivityMin !== undefined || filter.daysSinceActivityMax !== undefined;
  const visitBounded = filter.daysSinceVisitMin !== undefined || filter.daysSinceVisitMax !== undefined;
  return records.filter((r) => {
    const s = statsOf(ctx.stats, r.id);
    if (filter.followUpDue && !(s.nextScheduledAt && s.nextScheduledAt.getTime() < endOfToday)) return false;
    if (activityBounded) {
      const d = daysSinceActivity(s, now);
      if (filter.daysSinceActivityMin !== undefined && d !== undefined && d < filter.daysSinceActivityMin) return false;
      if (filter.daysSinceActivityMax !== undefined && (d === undefined || d > filter.daysSinceActivityMax)) return false;
    }
    if (types.size > 0 && !types.has(r.type)) return false;
    if (pipelineIds.size > 0 && !hasId(pipelineIds, pipelineFor(r, ctx.pipelines)?.id)) return false;
    if (stageIds.size > 0 && !hasId(stageIds, r.stageId)) return false;
    if (resultIds.size > 0 && !hasId(resultIds, s.lastVisitResultId)) return false;
    if (priorities.size > 0 && !priorities.has(r.priority)) return false;
    if (ownerBounded && !(r.ownerId ? hasId(ownerIds, r.ownerId) : filter.includeUnassigned)) return false;
    if (territoryIds.size > 0 && !hasId(territoryIds, r.territoryId)) return false;
    if (filter.onlyMine && !sameId(r.ownerId, ctx.viewerId)) return false;
    if (filter.neverVisited && s.lastVisitAt !== undefined) return false;
    if (filter.hasScheduledActivity && s.scheduledCount === 0) return false;
    if (tags.size > 0 && !r.tags.some((t) => tags.has(t))) return false;
    if (visitBounded) {
      const d = daysSinceVisit(s, now);
      if (d === undefined) return false;
      if (filter.daysSinceVisitMin !== undefined && d < filter.daysSinceVisitMin) return false;
      if (filter.daysSinceVisitMax !== undefined && d > filter.daysSinceVisitMax) return false;
    }
    return true;
  });
}

// MARK: - Quick presets (MapFilterSheet.QuickPreset)

export const FILTER_PRESETS = ['highPriority', 'hotLeads', 'followUp', 'notHomeCallback', 'noActivity30'] as const;
export type FilterPreset = (typeof FILTER_PRESETS)[number];

export const FILTER_PRESET_TITLES: Readonly<Record<FilterPreset, string>> = {
  highPriority: 'High Priority',
  hotLeads: 'Hot Leads',
  followUp: 'Follow-up Needed',
  notHomeCallback: 'Not Home – Callback Due',
  noActivity30: 'No Activity 30 Days',
};

export const FILTER_PRESET_COLORS: Readonly<Record<FilterPreset, string>> = {
  highPriority: '#DB4038',
  hotLeads: '#F96A3C',
  followUp: '#2673E6',
  notHomeCallback: '#F5B400',
  noActivity30: '#8C919E',
};

/** Visit results whose name contains "not home" (case-insensitive). */
export function notHomeResultIds(visitResults: readonly VisitResultPayload[]): string[] {
  return visitResults.filter((r) => r.name.toLowerCase().includes('not home')).map((r) => r.id);
}

function sameStrings<T extends string>(a: readonly T[], b: readonly T[]): boolean {
  const x = new Set(a);
  const y = new Set(b);
  return x.size === y.size && [...x].every((v) => y.has(v));
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return sameStrings(a.map(idKey), b.map(idKey));
}

/** Whether a preset's criteria are what the filter has (the chip shows as on). */
export function isPresetOn(f: MapFilter, preset: FilterPreset, visitResults: readonly VisitResultPayload[]): boolean {
  switch (preset) {
    case 'highPriority':
      return sameStrings(f.priorities, ['A']);
    case 'hotLeads':
      return sameStrings(f.types, ['lead']) && sameStrings(f.priorities, ['A', 'B']);
    case 'followUp':
      return f.followUpDue && f.visitResultIds.length === 0;
    case 'notHomeCallback': {
      const ids = notHomeResultIds(visitResults);
      return f.followUpDue && ids.length > 0 && sameIds(f.visitResultIds, ids);
    }
    case 'noActivity30':
      return f.daysSinceActivityMin === 30 && f.daysSinceActivityMax === undefined;
  }
}

/** Tapping a preset chip: sets its criteria, or clears them when it was on. Other criteria are untouched. */
export function togglePreset(f: MapFilter, preset: FilterPreset, visitResults: readonly VisitResultPayload[]): MapFilter {
  const on = isPresetOn(f, preset, visitResults);
  const next: MapFilter = { ...f };
  switch (preset) {
    case 'highPriority':
      next.priorities = on ? [] : ['A'];
      break;
    case 'hotLeads':
      next.types = on ? [] : ['lead'];
      next.priorities = on ? [] : ['A', 'B'];
      break;
    case 'followUp':
      next.followUpDue = !on;
      if (!on) next.visitResultIds = [];
      break;
    case 'notHomeCallback':
      next.followUpDue = !on;
      next.visitResultIds = on ? [] : notHomeResultIds(visitResults);
      break;
    case 'noActivity30':
      if (on) delete next.daysSinceActivityMin;
      else next.daysSinceActivityMin = 30;
      delete next.daysSinceActivityMax;
      break;
  }
  return next;
}

// MARK: - Filter <-> URL query (ids only, never the search text: WEB_PLAN.md §5.5)

const Q = {
  type: 'type',
  pipeline: 'pipeline',
  stage: 'stage',
  result: 'result',
  priority: 'priority',
  owner: 'owner',
  territory: 'territory',
  tag: 'tag',
  visitMin: 'visitMin',
  visitMax: 'visitMax',
  activityMin: 'activityMin',
  activityMax: 'activityMax',
  mine: 'mine',
  never: 'never',
  scheduled: 'scheduled',
  followUp: 'followUp',
} as const;

/** The "Unassigned" owner in `owner=` query values. */
export const UNASSIGNED_OWNER_PARAM = 'none';

/** Query parameters for a filter (repeated keys for lists). */
export function filterToQuery(f: MapFilter): URLSearchParams {
  const p = new URLSearchParams();
  for (const t of f.types) p.append(Q.type, t);
  for (const id of f.pipelineIds) p.append(Q.pipeline, id);
  for (const id of f.stageIds) p.append(Q.stage, id);
  for (const id of f.visitResultIds) p.append(Q.result, id);
  for (const pr of f.priorities) p.append(Q.priority, pr);
  for (const id of f.ownerIds) p.append(Q.owner, id);
  if (f.includeUnassigned) p.append(Q.owner, UNASSIGNED_OWNER_PARAM);
  for (const id of f.territoryIds) p.append(Q.territory, id);
  for (const t of f.tags) p.append(Q.tag, t);
  if (f.daysSinceVisitMin !== undefined) p.set(Q.visitMin, String(f.daysSinceVisitMin));
  if (f.daysSinceVisitMax !== undefined) p.set(Q.visitMax, String(f.daysSinceVisitMax));
  if (f.daysSinceActivityMin !== undefined) p.set(Q.activityMin, String(f.daysSinceActivityMin));
  if (f.daysSinceActivityMax !== undefined) p.set(Q.activityMax, String(f.daysSinceActivityMax));
  if (f.onlyMine) p.set(Q.mine, '1');
  if (f.neverVisited) p.set(Q.never, '1');
  if (f.hasScheduledActivity) p.set(Q.scheduled, '1');
  if (f.followUpDue) p.set(Q.followUp, '1');
  return p;
}

function uniq<T>(xs: T[]): T[] {
  return [...new Set(xs)];
}

function dayCount(v: string | null): number | undefined {
  if (v === null || !/^\d{1,5}$/.test(v)) return undefined;
  return Number(v);
}

/** Reads a filter back from the URL; unknown values and malformed ids are ignored. */
export function filterFromQuery(p: URLSearchParams): MapFilter {
  const ids = (key: string) => uniq(p.getAll(key).filter(isUuid));
  const owners = p.getAll(Q.owner);
  const f: MapFilter = {
    types: uniq(p.getAll(Q.type).filter((t): t is RecordType => (RECORD_TYPES as readonly string[]).includes(t))),
    pipelineIds: ids(Q.pipeline),
    stageIds: ids(Q.stage),
    visitResultIds: ids(Q.result),
    priorities: uniq(p.getAll(Q.priority).filter((t): t is Priority => (PRIORITIES as readonly string[]).includes(t))),
    ownerIds: uniq(owners.filter(isUuid)),
    includeUnassigned: owners.includes(UNASSIGNED_OWNER_PARAM),
    territoryIds: ids(Q.territory),
    tags: uniq(p.getAll(Q.tag).filter((t) => t !== '')),
    onlyMine: p.get(Q.mine) === '1',
    neverVisited: p.get(Q.never) === '1',
    hasScheduledActivity: p.get(Q.scheduled) === '1',
    followUpDue: p.get(Q.followUp) === '1',
  };
  const vMin = dayCount(p.get(Q.visitMin));
  const vMax = dayCount(p.get(Q.visitMax));
  const aMin = dayCount(p.get(Q.activityMin));
  const aMax = dayCount(p.get(Q.activityMax));
  if (vMin !== undefined) f.daysSinceVisitMin = vMin;
  if (vMax !== undefined) f.daysSinceVisitMax = vMax;
  if (aMin !== undefined) f.daysSinceActivityMin = aMin;
  if (aMax !== undefined) f.daysSinceActivityMax = aMax;
  return f;
}

// MARK: - Sorting (RecordListSort)

export const RECORD_LIST_SORTS = [
  'daysSinceVisitLeast',
  'daysSinceVisitMost',
  'name',
  'stage',
  'priority',
  'value',
  'distance',
  'lastActivity',
] as const;
export type RecordListSort = (typeof RECORD_LIST_SORTS)[number];

export const RECORD_LIST_SORT_TITLES: Readonly<Record<RecordListSort, string>> = {
  daysSinceVisitLeast: 'Days Since Last Visit: Least first',
  daysSinceVisitMost: 'Days Since Last Visit: Most first',
  name: 'Name',
  stage: 'Stage',
  priority: 'Priority',
  value: 'Value',
  distance: 'Distance from me',
  lastActivity: 'Last activity',
};

export interface SortContext {
  stats?: DerivedStats;
  pipelines: readonly PipelinePayload[];
  now?: Date;
  /** The point "Distance from me" measures from; without one that sort falls back to name. */
  here?: Coordinate | null;
}

/**
 * A comparator for `RecordListSort` (MapTab+Store.sorted). Never-visited stores sort last in both days-since-visit
 * orders; ties keep the input order (Array#sort is stable).
 */
export function recordComparator(sort: RecordListSort, ctx: SortContext): (a: RecordPayload, b: RecordPayload) => number {
  const now = ctx.now ?? new Date();
  const days = (r: RecordPayload) => daysSinceVisit(statsOf(ctx.stats, r.id), now);
  const byName = (a: RecordPayload, b: RecordPayload) => compareLocalized(recordDisplayName(a), recordDisplayName(b));
  switch (sort) {
    case 'daysSinceVisitLeast':
      return (a, b) =>
        (days(a) ?? MAX_ORDER) - (days(b) ?? MAX_ORDER) || compareScalars(recordDisplayName(a), recordDisplayName(b));
    case 'daysSinceVisitMost':
      return (a, b) => (days(b) ?? -1) - (days(a) ?? -1);
    case 'name':
      return byName;
    case 'stage':
      return (a, b) =>
        (stageFor(a, ctx.pipelines)?.order ?? MAX_ORDER) - (stageFor(b, ctx.pipelines)?.order ?? MAX_ORDER) || byName(a, b);
    case 'priority':
      return (a, b) => (PRIORITY_SCORES[b.priority] ?? 0) - (PRIORITY_SCORES[a.priority] ?? 0) || b.value - a.value;
    case 'value':
      return (a, b) => b.value - a.value;
    case 'distance': {
      const here = ctx.here;
      if (!here) return byName;
      const dist = (r: RecordPayload) => (r.coordinate ? distanceMeters(r.coordinate, here) : Infinity);
      return (a, b) => {
        const da = dist(a);
        const db = dist(b);
        return da === db ? 0 : da < db ? -1 : 1;
      };
    }
    case 'lastActivity':
      return (a, b) =>
        (statsOf(ctx.stats, b.id).lastActivityAt?.getTime() ?? -Infinity) -
          (statsOf(ctx.stats, a.id).lastActivityAt?.getTime() ?? -Infinity) || 0;
  }
}

/** `AppStore.sorted(_:by:from:)` (a sorted copy). */
export function sortRecords<R extends RecordPayload>(records: readonly R[], sort: RecordListSort, ctx: SortContext): R[] {
  const cmp = recordComparator(sort, ctx);
  return [...records].sort(cmp);
}

// MARK: - History, upcoming, titles

export type HistoryEntry =
  | { kind: 'activity'; id: string; date: Date; activity: ActivityPayload }
  | { kind: 'message'; id: string; date: Date; comm: CommPayload };

/** `historyEntries(for:)`: the record's non-scheduled activities plus inbound messages, newest first. */
export function historyEntries(
  recordId: string,
  activities: readonly ActivityPayload[],
  comms: readonly CommPayload[],
): HistoryEntry[] {
  const out: HistoryEntry[] = [];
  for (const a of activities) {
    if (!sameId(a.recordId, recordId) || a.status === 'scheduled') continue;
    out.push({ kind: 'activity', id: a.id, date: new Date(effectiveMs(a)), activity: a });
  }
  for (const m of comms) {
    if (!sameId(m.recordId, recordId) || m.direction !== 'inbound') continue;
    out.push({ kind: 'message', id: m.id, date: new Date(ms(m.sentAt) ?? 0), comm: m });
  }
  return out.sort((x, y) => y.date.getTime() - x.date.getTime());
}

export const HISTORY_FILTERS = ['all', 'visits', 'comms', 'notes', 'changes'] as const;
export type HistoryFilter = (typeof HISTORY_FILTERS)[number];

export const HISTORY_FILTER_TITLES: Readonly<Record<HistoryFilter, string>> = {
  all: 'All',
  visits: 'Visits',
  comms: 'Comms',
  notes: 'Notes',
  changes: 'Changes',
};

/** `HistoryFilter.matches`. */
export function historyFilterMatches(filter: HistoryFilter, entry: HistoryEntry): boolean {
  if (filter === 'all') return true;
  if (entry.kind === 'message') return filter === 'comms';
  const t = entry.activity.type;
  switch (filter) {
    case 'visits':
      return t === 'visit' || t === 'meeting';
    case 'comms':
      return t === 'call' || t === 'email' || t === 'text';
    case 'notes':
      return t === 'note' || t === 'photo' || t === 'document' || t === 'task';
    case 'changes':
      return t === 'stageChange' || t === 'assignment' || t === 'created';
  }
}

/** `upcomingActivities(for:)`: the record's scheduled activities, soonest first (no time last). */
export function upcomingActivities(recordId: string, activities: readonly ActivityPayload[]): ActivityPayload[] {
  return activities
    .filter((a) => sameId(a.recordId, recordId) && a.status === 'scheduled')
    .sort((a, b) => (ms(a.scheduledAt) ?? Infinity) - (ms(b.scheduledAt) ?? Infinity) || 0);
}

/** `Activity.isOverdue`: scheduled and its time has passed. */
export function isOverdue(a: Pick<ActivityPayload, 'status' | 'scheduledAt'>, now: Date = new Date()): boolean {
  if (a.status !== 'scheduled') return false;
  const at = ms(a.scheduledAt);
  return at !== undefined && at < now.getTime();
}

/** Overdue scheduled activities (dashboard "Needs attention"), oldest first. */
export function overdueActivities(activities: readonly ActivityPayload[], now: Date = new Date()): ActivityPayload[] {
  return activities
    .filter((a) => isOverdue(a, now))
    .sort((a, b) => (ms(a.scheduledAt) ?? 0) - (ms(b.scheduledAt) ?? 0));
}

/**
 * `atRiskRecords`: open stores (not in a won or lost stage) with no activity for `staleDays` days, measured from
 * `lastActivityAt ?? createdAt`; most valuable first.
 */
export function atRiskRecords<R extends RecordPayload>(
  records: readonly R[],
  ctx: { stats?: DerivedStats; pipelines: readonly PipelinePayload[]; now?: Date; staleDays?: number },
): R[] {
  const cutoff = addDays(ctx.now ?? new Date(), -(ctx.staleDays ?? 30)).getTime();
  return records
    .filter((r) => {
      const st = stageFor(r, ctx.pipelines);
      if (st?.isWon || st?.isLost) return false;
      const last = statsOf(ctx.stats, r.id).lastActivityAt?.getTime() ?? ms(r.createdAt) ?? 0;
      return last < cutoff;
    })
    .sort((a, b) => b.value - a.value);
}

/** `Components+Store.displayTitle(for:)`: "On-Site Visit · Contacted", "Stage Changed", "Call · Left voicemail"… */
export function activityDisplayTitle(
  activity: Pick<ActivityPayload, 'type' | 'title' | 'outcome' | 'resultId'>,
  visitResults: ReadonlyMap<string, Pick<VisitResultPayload, 'name'>>,
): string {
  switch (activity.type) {
    case 'visit': {
      const name = lookup(visitResults, activity.resultId)?.name;
      if (name) return `On-Site Visit · ${name}`;
      return activity.title === '' || activity.title === ACTIVITY_TITLE.visit ? 'On-Site Visit' : activity.title;
    }
    case 'stageChange':
      return 'Stage Changed';
    case 'assignment':
      return 'Assigned';
    default: {
      const typeTitle = ACTIVITY_TITLE[activity.type];
      if (activity.title === '') return typeTitle;
      return activity.title === typeTitle && activity.outcome !== '' ? `${activity.title} · ${activity.outcome}` : activity.title;
    }
  }
}

/** `allTags`: every tag on the given stores, de-duplicated case-insensitively, alphabetical. */
export function allTags(records: readonly RecordPayload[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of records) {
    for (const t of r.tags) {
      if (t === '') continue;
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
  }
  return out.sort(compareLocalized);
}

/**
 * `cardFieldsLine(for:)`: "Roof Type: Metal · Contract value: $12,000", the custom fields flagged "Show on card" for
 * the record's type with a value, in field order; "" when there is nothing to show.
 */
export function cardFieldsLine(
  record: RecordPayload,
  customFields: readonly CustomFieldPayload[],
  currencyCode: string,
  locale?: string,
): string {
  const values = customValuesToMap(record.customValues);
  return customFields
    .filter((f) => f.showOnCard && f.appliesTo.includes(record.type))
    .sort((a, b) => a.order - b.order)
    .map((f) => {
      const v = values.get(f.id.toUpperCase());
      if (!v) return null;
      const text = f.type === 'currency' && v.kind === 'number' ? formatMoney(v.value, currencyCode, locale) : fieldValueDisplay(v, locale);
      return text === '' ? null : `${f.name}: ${text}`;
    })
    .filter((s): s is string => s !== null)
    .join(' · ');
}
